"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defineEntityFiles = void 0;
const zod_1 = require("zod");
const contracts_core_1 = require("@omniflow/contracts-core");
const entity_file_schemas_1 = require("./entity-file-schemas");
/**
 * Генерирует типизированные NATS routes для файловых вложений entity.
 *
 * Каждый вызов создаёт 9 routes:
 *   cmd.<entity>.file.upload   — initiate upload (pending row + staging key)
 *   cmd.<entity>.file.confirm  — validate + move to final S3
 *   cmd.<entity>.file.delete   — soft/hard delete per slot config
 *   q.<entity>.files.list      — metadata list
 *   q.<entity>.file.presign    — presigned download URL
 *   q.<entity>.file.config     — slot config query
 *   evt.<entity>.file.uploaded — event after successful upload
 *   evt.<entity>.file.deleted  — event after delete
 *   evt.<entity>.file.downloaded — audit event after presign
 *
 * @example
 *   const TicketFileRoutes = defineEntityFiles('ticket', {
 *     attachments: { maxCount: 10, maxSizeMb: 10, accept: ['image/*', 'application/pdf'] },
 *     avatar: { maxCount: 1, maxSizeMb: 2, accept: ['image/*'], versioned: true },
 *   });
 *
 *   export const TicketRoutes = {
 *     ...TicketEntity.routes,
 *     ...TicketFileRoutes,
 *   } as const;
 */
const defineEntityFiles = (entityName, slots) => {
    validateSlots(slots);
    const pascalEntity = entityName.charAt(0).toUpperCase() + entityName.slice(1);
    const fileUpload = (0, contracts_core_1.defineCommand)({
        subject: `cmd.${entityName}.file.upload`,
        input: contracts_core_1.CommandMessage.extend({
            type: zod_1.z.literal(`${pascalEntity}FileUploadCommand`),
            version: zod_1.z.literal(1),
            entityId: zod_1.z.string().uuid(),
            slot: zod_1.z.string(),
            originalName: zod_1.z.string(),
            contentType: zod_1.z.string(),
            sizeBytes: zod_1.z.number().int().positive(),
        }),
        reply: zod_1.z.discriminatedUnion('isOk', [
            zod_1.z.object({
                isOk: zod_1.z.literal(true),
                fileId: zod_1.z.string().uuid(),
                stagingKey: zod_1.z.string(),
            }),
            zod_1.z.object({
                isOk: zod_1.z.literal(false),
                errorCode: zod_1.z.enum(entity_file_schemas_1.EntityFileErrorCodes),
                error: zod_1.z.string().optional(),
                details: zod_1.z.record(zod_1.z.unknown()).optional(),
            }),
        ]),
    });
    const fileConfirm = (0, contracts_core_1.defineCommand)({
        subject: `cmd.${entityName}.file.confirm`,
        input: contracts_core_1.CommandMessage.extend({
            type: zod_1.z.literal(`${pascalEntity}FileConfirmCommand`),
            version: zod_1.z.literal(1),
            fileId: zod_1.z.string().uuid(),
        }),
        reply: zod_1.z.discriminatedUnion('isOk', [
            zod_1.z.object({
                isOk: zod_1.z.literal(true),
                file: entity_file_schemas_1.EntityFileMetaSchema,
            }),
            zod_1.z.object({
                isOk: zod_1.z.literal(false),
                errorCode: zod_1.z.enum(entity_file_schemas_1.EntityFileErrorCodes),
                error: zod_1.z.string().optional(),
                details: zod_1.z.record(zod_1.z.unknown()).optional(),
            }),
        ]),
    });
    const fileDelete = (0, contracts_core_1.defineCommand)({
        subject: `cmd.${entityName}.file.delete`,
        input: contracts_core_1.CommandMessage.extend({
            type: zod_1.z.literal(`${pascalEntity}FileDeleteCommand`),
            version: zod_1.z.literal(1),
            fileId: zod_1.z.string().uuid(),
        }),
        reply: zod_1.z.discriminatedUnion('isOk', [
            zod_1.z.object({ isOk: zod_1.z.literal(true) }),
            zod_1.z.object({
                isOk: zod_1.z.literal(false),
                errorCode: zod_1.z.enum(entity_file_schemas_1.EntityFileErrorCodes),
                error: zod_1.z.string().optional(),
            }),
        ]),
    });
    const filesList = (0, contracts_core_1.defineQuery)({
        subject: `q.${entityName}.files.list`,
        input: contracts_core_1.QueryMessage.extend({
            type: zod_1.z.literal(`${pascalEntity}FilesListQuery`),
            version: zod_1.z.literal(1),
            entityId: zod_1.z.string().uuid(),
            slot: zod_1.z.string().optional(),
            limit: zod_1.z.number().int().positive().max(1000).optional(),
            offset: zod_1.z.number().int().nonnegative().optional(),
            includeAllVersions: zod_1.z.boolean().optional(),
        }),
        reply: zod_1.z.discriminatedUnion('isOk', [
            zod_1.z.object({
                isOk: zod_1.z.literal(true),
                items: zod_1.z.array(entity_file_schemas_1.EntityFileMetaSchema),
                total: zod_1.z.number().int().nonnegative(),
            }),
            zod_1.z.object({
                isOk: zod_1.z.literal(false),
                errorCode: zod_1.z.enum(entity_file_schemas_1.EntityFileErrorCodes),
                error: zod_1.z.string().optional(),
            }),
        ]),
    });
    const filePresign = (0, contracts_core_1.defineQuery)({
        subject: `q.${entityName}.file.presign`,
        input: contracts_core_1.QueryMessage.extend({
            type: zod_1.z.literal(`${pascalEntity}FilePresignQuery`),
            version: zod_1.z.literal(1),
            fileId: zod_1.z.string().uuid(),
        }),
        reply: zod_1.z.discriminatedUnion('isOk', [
            zod_1.z.object({
                isOk: zod_1.z.literal(true),
                presignedUrl: zod_1.z.string(),
                fileName: zod_1.z.string(),
                contentType: zod_1.z.string(),
                sizeBytes: zod_1.z.number().int().nonnegative(),
            }),
            zod_1.z.object({
                isOk: zod_1.z.literal(false),
                errorCode: zod_1.z.enum(entity_file_schemas_1.EntityFileErrorCodes),
                error: zod_1.z.string().optional(),
            }),
        ]),
    });
    const fileConfig = (0, contracts_core_1.defineQuery)({
        subject: `q.${entityName}.file.config`,
        input: contracts_core_1.QueryMessage.extend({
            type: zod_1.z.literal(`${pascalEntity}FileConfigQuery`),
            version: zod_1.z.literal(1),
        }),
        reply: zod_1.z.object({
            isOk: zod_1.z.literal(true),
            slots: zod_1.z.record(zod_1.z.object({
                maxCount: zod_1.z.number().int().positive(),
                maxSizeMb: zod_1.z.number().positive(),
                accept: zod_1.z.array(zod_1.z.string()),
                deleteMode: zod_1.z.enum(['soft', 'hard']).optional(),
                versioned: zod_1.z.boolean().optional(),
            })),
        }),
    });
    const fileUploaded = (0, contracts_core_1.defineEvent)(`evt.${entityName}.file.uploaded`, contracts_core_1.EventMessage.extend({
        type: zod_1.z.literal(`${pascalEntity}FileUploadedEvent`),
        version: zod_1.z.literal(1),
        file: entity_file_schemas_1.EntityFileMetaSchema,
    }));
    const fileDeleted = (0, contracts_core_1.defineEvent)(`evt.${entityName}.file.deleted`, contracts_core_1.EventMessage.extend({
        type: zod_1.z.literal(`${pascalEntity}FileDeletedEvent`),
        version: zod_1.z.literal(1),
        fileId: zod_1.z.string().uuid(),
        entityId: zod_1.z.string().uuid(),
        slot: zod_1.z.string(),
    }));
    const fileDownloaded = (0, contracts_core_1.defineEvent)(`evt.${entityName}.file.downloaded`, contracts_core_1.EventMessage.extend({
        type: zod_1.z.literal(`${pascalEntity}FileDownloadedEvent`),
        version: zod_1.z.literal(1),
        fileId: zod_1.z.string().uuid(),
        entityId: zod_1.z.string().uuid(),
        slot: zod_1.z.string(),
        userId: zod_1.z.string(),
    }));
    const routes = {
        fileUpload,
        fileConfirm,
        fileDelete,
        filesList,
        filePresign,
        fileConfig,
        fileUploaded,
        fileDeleted,
        fileDownloaded,
    };
    return {
        entityType: entityName,
        fileSlots: slots,
        routes,
    };
};
exports.defineEntityFiles = defineEntityFiles;
const validateSlots = (slots) => {
    for (const [name, config] of Object.entries(slots)) {
        if (config.versioned && config.maxCount > 1) {
            throw new Error(`FileSlot "${name}": versioned=true requires maxCount=1, got maxCount=${config.maxCount}`);
        }
    }
};
//# sourceMappingURL=define-entity-file-routes.js.map